import { createActions, createReducer } from "reduxsauce"
import { v4 as uuid } from "uuid"
import moment from "moment"
import { filter, get, first, find, set, isEmpty, size } from "lodash"
import { take, takeLatest, select, put, call, getContext } from "redux-saga/effects"
import { post } from "api"
import uploadFiles from "api/file-upload"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as serverNotificationsCreators } from "modules/server-notifications/model"
import { Creators as dialogCreators } from "modules/dialog/model"
import { Creators as experimentsCreators } from "modules/experiments/model"
import { Creators as modelCreators } from "model/data"
import { Creators as resourcesCreators } from "modules/resources/model"
import { Creators as tagAndAttributeCreators, Types as TagAndAttributeTypes } from "modules/tag-and-attribute-creator/model"
import { getExperiment } from "./selectors"
import { log } from "helpers/logger"

export const { Types, Creators } = createActions({
  submitStepSuccess: ["step"],
  addFiles: ["files"],
  removeFile: ["file"],
  scanFiles: ["files", "portfolioDetails"],
  scanFilesSuccess: ["result", "uploadedFiles"],
  scanFilesError: [],
  confirmUpload: ["uploadOption", "portfolioDetails"],
  confirmUploadError: [],
  resetUpload: [],
  resetScan: [],
  changeUploadOption: ["uploadOption"],
}, {
  prefix: "ompnt/modules/portfolio-positions/",
})

const INITIAL_STATE = {
  activeStep: 0,
  scanResult: null,
  files: [],
  uploadedFiles: [],
  uploadOption: "",
  uploadDateTime: null,
}

function handleSubmitStepSuccess(state, step) {
  return { ...state, activeStep: step }
}

function handleAddFiles(state, { files }) {
  const newFiles = files.map(file => ({ id: uuid(), file }))
  return { ...state, files: [...state.files, ...newFiles] }
}

function handleRemoveFile(state, { file: { id } }) {
  return { ...state, files: filter(state.files, file => file.id !== id) }
}

function handleScanFiles(state) {
  return { ...state }
}

function handleScanFilesSuccess(state, { result, uploadedFiles }) {
  return { ...state, activeStep: 1, scanResult: result, uploadDateTime: moment(), uploadedFiles }
}

function handleScanFilesError(state) {
  return { ...state, scanResult: null }
}

function handleConfirmUpload(state) {
  return { ...state }
}

function handleConfirmUploadError(state) {
  return { ...state }
}

function handleResetScan(state) {
  return { ...state, scanResult: null, activeStep: 0, uploadOption: "", uploadDateTime: null }
}

function handleResetUpload(state) {
  return { ...state, scanResult: null, files: [], activeStep: 0, uploadOption: "", uploadDateTime: null }
}

function handleChangeUploadOption(state, { uploadOption }) {
  return { ...state, uploadOption }
}

export const HANDLERS = {
  [Types.SUBMIT_STEP_SUCCESS]: handleSubmitStepSuccess,
  [Types.ADD_FILES]: handleAddFiles,
  [Types.REMOVE_FILE]: handleRemoveFile,
  [Types.SCAN_FILES]: handleScanFiles,
  [Types.SCAN_FILES_SUCCESS]: handleScanFilesSuccess,
  [Types.SCAN_FILES_ERROR]: handleScanFilesError,
  [Types.CONFIRM_UPLOAD]: handleConfirmUpload,
  [Types.CONFIRM_UPLOAD_ERROR]: handleConfirmUploadError,
  [Types.RESET_UPLOAD]: handleResetUpload,
  [Types.RESET_SCAN]: handleResetScan,
  [Types.CHANGE_UPLOAD_OPTION]: handleChangeUploadOption,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* scanFiles({ files, portfolioDetails }) {
  try {
    if (files.length > 0) {
      const { account, portfolioId, experiment } = yield select(state => ({
        account: state.global.selected.account,
        portfolioId: state.global.selected.portfolio,
        experiment: getExperiment(state),
      }))

      yield put(dialogCreators.open({ title: "Your Files Are Being Scanned", contentText: "Please wait while we scan your data", disableEscapeKeyDown: true, loadingDialog: true, disableBackdropClick: true }))

      const isExperiment = experiment && experiment.id
      let resourceId, resourceType, doctorURL
      if (isExperiment) {
        resourceId = experiment.id
        resourceType = "experiment"
        doctorURL = `/api/${account}/experiments/${experiment.id}/portfolio-doctor`
      } else {
        resourceId = portfolioDetails.id
        resourceType = "portfolio"
        doctorURL = `/api/${account}/p/${resourceId}/portfolio-doctor`
      }

      const uploadedFiles = yield call(uploadFiles, account, resourceId, files, { type: resourceType })

      if (size(uploadedFiles)) {
        const doctorParams = { filepaths: uploadedFiles }

        if (isExperiment) {
          doctorParams.modelId = portfolioDetails.modelId
        }

        let retry, portfolioDoctorResult, abort
        // Portfolio doctor call error loop
        // Break early unless its a uninitialized tags error
        do {
          // Typical workflow doesn't retry the pdoctor call
          retry = false
          const portfolioDoctorReturn = yield call(post, doctorURL, doctorParams)
          portfolioDoctorResult = get(portfolioDoctorReturn, "portfolioDoctorResult")
          // If the pdoctor returns nothing, no retry, exit loop early
          if (!portfolioDoctorResult) {
            yield put(Creators.scanFilesError())
            yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
            break
          }

          // If there are no errors, no retry, exit loop early
          if (isEmpty(portfolioDoctorResult.errors)) {
            break
          }

          // If errors that are not uninitialized tag/attributes categories, we dont need to retry, exit loop early
          const uninitializedTagError = find(portfolioDoctorResult.errors, (error) => error.code === "E40201")
          const uninitializedAttributesError = find(portfolioDoctorResult.errors, error => error.code === "E40409")
          if (!uninitializedTagError && !uninitializedAttributesError) {
            yield put(Creators.scanFilesError())
            yield put(notificationCreators.add("error", null, get(first(portfolioDoctorResult.errors), "message")))
            abort = true
            break
          }

          // The missing tags/attributes are returned by pdoctor
          // Display the tag/attribute creation UI and wait for the process to finish
          const tags = get(uninitializedTagError, "details")
          const attributes = get(uninitializedAttributesError, "details")
          yield put(tagAndAttributeCreators.createTagsOrAttributesStart(tags, attributes))

          const { type } = yield take([TagAndAttributeTypes.CREATE_TAGS_OR_ATTRIBUTES_SUCCESS, TagAndAttributeTypes.CREATE_TAGS_OR_ATTRIBUTES_ERROR, TagAndAttributeTypes.CREATE_TAGS_OR_ATTRIBUTES_CANCEL])
          if (type === TagAndAttributeTypes.CREATE_TAGS_OR_ATTRIBUTES_ERROR) {
            yield put(Creators.scanFilesError())
            yield put(notificationCreators.add("error", null, `An error occured while trying to create your new ${attributes ? "attribute" : "tag"} categories. Try scanning your files again.`, "message"))
            break
          } else if (type === TagAndAttributeTypes.CREATE_TAGS_OR_ATTRIBUTES_CANCEL) {
            yield put(Creators.scanFilesError())
            abort = true
            break
          } else {
            // We only support creating either tags OR attributes not both
            // set dropUninitializedCategoryColumns true for the next pdoctor call
            set(doctorParams, "dropUninitializedCategoryColumns", true)
            retry = true
          }
          // Display other errors if we are not retrying the pdoctor
          if (!retry) {
            yield put(Creators.scanFilesError())
            yield put(notificationCreators.add("error", null, get(first(portfolioDoctorResult.errors), "message")))
          }
        } while (retry)

        if (abort) {
          yield put(dialogCreators.close())
          return
        }

        if (get(portfolioDoctorResult, "data")) {
          yield put(Creators.scanFilesSuccess(portfolioDoctorResult.data, uploadedFiles))

          const { navigate } = yield getContext("router")
          if (experiment && experiment.id) {
            yield call(navigate, `/${account}/p/${portfolioId}/experiments/${experiment.id}/upload/preview`)
          } else {
            yield call(navigate, `/${account}/p/${portfolioId}/upload/preview`)
          }
        }

        if (!isEmpty(get(portfolioDoctorResult, "messages"))) {
          yield put(notificationCreators.add("warning", null, get(first(portfolioDoctorResult.messages), "message")))
        }
      } else {
        yield put(Creators.scanFilesError())
        yield put(notificationCreators.add("error", null, "Something bad happened while uploading the files."))
      }
      yield put(dialogCreators.close())
    }
  } catch (err) {
    let logErr = err
    if (!(err instanceof Error)) {
      logErr = new Error(JSON.stringify(err))
    }

    yield call(log, logErr)
    yield put(Creators.scanFilesError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
    yield put(dialogCreators.close())
  }
}

function* confirmUpload({ uploadOption, portfolioDetails }) {
  try {
    const { uploadedFiles, account, portfolioId, experiment } = yield select(state => ({
      uploadedFiles: state.modules.portfolioPositions.uploadedFiles,
      portfolioId: state.global.selected.portfolio,
      account: state.global.selected.account,
      experiment: getExperiment(state),
    }))

    yield put(dialogCreators.open({
      title: uploadedFiles.length > 1 ? "Your Files Are Being Uploaded" : "Your File Is Being Uploaded",
      contentText: "Please wait while we upload your data",
      disableEscapeKeyDown: true,
      loadingDialog: true,
      disableBackdropClick: true,
    }))

    let doctorURL
    const deleteDates = uploadOption === "clear"
    // dropUninitializedCategoryColumns is always true because you need to go through the scanFiles saga first
    // in which you will either drop the columns or create them.
    const doctorParams = { filepaths: uploadedFiles, upload: true, deleteDates, dropUninitializedCategoryColumns: true }

    if (experiment && experiment.id) {
      doctorURL = `/api/${account}/experiments/${experiment.id}/portfolio-doctor`
      doctorParams.modelId = portfolioDetails.modelId
    } else {
      doctorURL = `/api/${account}/p/${portfolioId}/portfolio-doctor`
    }

    const { portfolioDoctorResult } = yield call(post, doctorURL, doctorParams)

    if (portfolioDoctorResult !== "OK") {
      yield put(Creators.confirmUploadError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
      yield put(dialogCreators.close())
    } else {
      yield put(dialogCreators.close())
      yield put(serverNotificationsCreators.open())
      yield put(Creators.resetUpload())

      const { navigate } = yield getContext("router")
      if (experiment && experiment.id) {
        yield put(modelCreators.resetCollections(["portfolios", "portfolioExperiments", "experiments"]))
        yield put(experimentsCreators.preLoadExperiment(experiment))
        yield call(navigate, `/${account}/p/${portfolioId}/rebalance/experiments`)
      } else {
        yield put(resourcesCreators.addUploadingItem(portfolioId))
        yield put(modelCreators.resetCollections(["portfolios", "portfolio", "portfolioModelMeta"]))
        yield call(navigate, `/${account}/home/resources`)
      }
    }
  } catch {
    yield put(Creators.confirmUploadError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading the positions."))
    yield put(dialogCreators.close())
  }
}

export function* saga() {
  yield takeLatest(Types.SCAN_FILES, scanFiles)
  yield takeLatest(Types.CONFIRM_UPLOAD, confirmUpload)
}
